import {messages} from '@smartmixin/i18n';
import {ApiErrorResponse, HEADERS} from 'apisauce';
import {StatusCodes} from 'http-status-codes';
import {jwtDecode} from 'jwt-decode';
import moment from 'moment-timezone';
import React from 'react';
import isEqual from 'react-fast-compare';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useUserLanguage} from './userContext';
import {ErrorCode, SUPPORTED_API_VERSION, apiClient, getApiUrl} from '../api';
import {ParsedApiError, parseClientError} from '../api/parseApiError';
import {ApiRequestParams, ApiResponse, TypedApiRequestParams} from '../api/types';

type FieldInfo = ParsedApiError['fields'][string];
import {
  getDebugEmail,
  selectIsAdmin,
  selectToken,
  setAlert,
  setUpgradeRequired,
  storeToken,
} from '../store';
import {JwtToken} from '../store/user/types';

export type ErrorInfo = {
  statusCode?: number;
  fields: Record<string, FieldInfo>;
  // Raw response body of CLIENT_ERROR responses, for callers that need
  // more than details/fields (e.g. the forecast-map build 409 payload
  // carrying a run object, or every message of a multi-error 400 field).
  data?: unknown;
  details?: {
    message: string;
    code: string | number;
  };
};

/**
 * Custom error class for API errors that extends Error.
 * This ensures proper error handling and Sentry reporting.
 */
export class ApiError extends Error {
  public readonly statusCode?: number;
  public readonly fields: Record<string, FieldInfo>;
  public readonly data?: unknown;
  public readonly details?: {
    message: string;
    code: string | number;
  };

  constructor(errorInfo: ErrorInfo) {
    super(errorInfo.details?.message || 'API Error');
    this.name = 'ApiError';
    this.statusCode = errorInfo.statusCode;
    this.fields = errorInfo.fields;
    this.data = errorInfo.data;
    this.details = errorInfo.details;
    // Maintains proper stack trace for where our error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

type DecodedJwtToken = {
  token_type: string;
  exp: number;
  jti: string;
  user_id: number;
};

type RefreshTokenResponse = {
  refresh: string;
  access: string;
};

// Lazy initialization to allow API URL configuration before first use
let _client: ReturnType<typeof apiClient> | null = null;
const getClient = () => {
  if (!_client) {
    _client = apiClient(getApiUrl());
  }
  return _client;
};

/**
 * Reset the cached API client so the next call to getClient()
 * creates a fresh instance with the current API URL.
 * Must be called after configureApiUrl() to take effect.
 */
export const resetApiClient = () => {
  _client = null;
};

const useApiClient = () => {
  const dispatch = useDispatch();
  const storedToken = useSelector(selectToken, isEqual) as JwtToken | null;
  const language = useUserLanguage();
  const [loading, setLoading] = React.useState(false);
  const {formatMessage} = useIntl();
  const debugEmail = useSelector(getDebugEmail);
  const isAdmin = useSelector(selectIsAdmin);

  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for stable fetch identity — values are read at call time,
  // so fetch doesn't need them in its dependency array.
  const storedTokenRef = React.useRef(storedToken);
  storedTokenRef.current = storedToken;
  const languageRef = React.useRef(language);
  languageRef.current = language;
  const formatMessageRef = React.useRef(formatMessage);
  formatMessageRef.current = formatMessage;
  const debugEmailRef = React.useRef(debugEmail);
  debugEmailRef.current = debugEmail;
  const isAdminRef = React.useRef(isAdmin);
  isAdminRef.current = isAdmin;

  function debounce<T extends (...args: unknown[]) => unknown>(
    f: T,
    interval: number,
  ) {
    return (...args: Parameters<T>) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return new Promise((resolve) => {
        timerRef.current = setTimeout(() => resolve(f(...args)), interval);
      });
    };
  }

  const fetch = React.useCallback(
    async <T = unknown>(
      apiCall: TypedApiRequestParams<T> | ApiRequestParams,
      options: {
        debounce?: number;
        ignore404?: boolean;
        ignore401?: boolean;
        // Suppress the global error alert for 409 responses the caller
        // handles inline (e.g. forecast-map build conflicts).
        ignore409?: boolean;
        // Suppress the global error alert for ALL failures of this call —
        // the promise still rejects with the same ApiError. For polling
        // loops that retry transient failures and surface terminal
        // failure inline themselves (a long-running build poll must not
        // raise a snackbar on every network blip).
        silent?: boolean;
        skipLoginAs?: boolean;
        accessToken?: string;
        headers?: HEADERS;
      } = {},
    ): Promise<T> => {
      const fmtMsg = formatMessageRef.current;
      const errorHandler = (errorRes: ApiErrorResponse<unknown>) => {
        const info: ErrorInfo = {
          fields: {},
        };
        if (errorRes.problem === 'CLIENT_ERROR') {
          info.statusCode = errorRes.status;
          info.data = errorRes.data;
          if (errorRes.status === StatusCodes.BAD_REQUEST) {
            const parsed = parseClientError(errorRes.data);
            info.details = parsed.details;
            info.fields = parsed.fields;
            return info;
          }
          const detail = (errorRes.data as Record<string, unknown>)?.detail;
          // Plain-string DRF details (e.g. the forecast-map build 409
          // conflict) would crash the alert gate below reading `.code` —
          // normalize them into the structured shape.
          info.details =
            typeof detail === 'string'
              ? {message: detail, code: `http_${errorRes.status}`}
              : (detail as {message: string; code: string | number}); // global error
          info.fields = {};
        } else {
          if (errorRes.problem === 'CONNECTION_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.connectionError),
              code: 'connectionError',
            };
          } else if (errorRes.problem === 'NETWORK_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.networkError),
              code: 'networkError',
            };
          } else if (errorRes.problem === 'TIMEOUT_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.timeoutError),
              code: 'timeoutError',
            };
          } else if (errorRes.problem === 'SERVER_ERROR') {
            if (errorRes.status === StatusCodes.SERVICE_UNAVAILABLE) {
              const detail = (errorRes.data as Record<string, unknown>)
                ?.detail;
              // Same plain-string normalization as the CLIENT_ERROR path.
              info.details =
                typeof detail === 'string'
                  ? {message: detail, code: `http_${errorRes.status}`}
                  : (detail as {message: string; code: string | number});
            } else {
              info.details = {
                message: fmtMsg(messages.network.serverError),
                code: 'serverError',
              };
            }
          } else if (errorRes.problem === 'CANCEL_ERROR') {
            info.details = {
              message: fmtMsg(messages.network.cancelError),
              code: 'cancelError',
            };
          } else {
            info.details = {
              message: fmtMsg(messages.network.unknownError),
              code: 'unknownError',
            };
          }
          info.fields = {};
        }
        return info;
      };

      const getToken = async (token: JwtToken | null) => {
        /* This is the automatic refresh token mechanism
        When getting the token, we ensure the stored access
        token is valid and if not, we refresh it using the
        stored refresh token */
        if (token) {
          const decodedAccessToken = jwtDecode(
            token.accessToken,
          ) as DecodedJwtToken;
          if (
            moment().unix() >
            moment.unix(decodedAccessToken.exp).unix() - 5 * 60
          ) {
            // Refresh Token in order to get a renewed token pair (access + refresh)
            const decodedRefreshToken = jwtDecode(
              token.refreshToken,
            ) as DecodedJwtToken;
            if (moment().unix() > moment.unix(decodedRefreshToken.exp).unix()) {
              dispatch({type: 'LOGOUT'});
              return '';
            }
            getClient().deleteHeader('Authorization');
            const res = (await getClient().post('auth/token/refresh/', {
              refresh: token.refreshToken,
            })) as ApiResponse<RefreshTokenResponse, unknown>;
            if (res.ok) {
              if (res.data) {
                const {refresh: refreshToken, access: accessToken} = res.data;
                dispatch(storeToken({accessToken, refreshToken}));
                return accessToken;
              } else {
                // We should never be there
                return '';
              }
            } else {
              const err = errorHandler(res as ApiErrorResponse<unknown>);

              if (err.details) {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }

              if (err.statusCode === StatusCodes.UNAUTHORIZED) {
                dispatch({type: 'LOGOUT'});
              }
              return Promise.reject(new ApiError(err));
            }
          } else {
            // stored accessToken is still valid, use it
            return token.accessToken;
          }
        } else {
          // There is not stored token
          return '';
        }
      };

      const fetchData = async (
        requestParams: ApiRequestParams,
        userToken: JwtToken | null,
      ) => {
        const client = getClient();
        client.setHeader('X-SmartMixin-Context', 'UI');
        client.setHeader('Accept-Language', `${languageRef.current}`);
        const {url, method, params} = requestParams;

        const token =
          options && options.accessToken
            ? options.accessToken
            : await getToken(userToken);
        // Do not use Authorization header if token is empty
        if (!token) {
          client.deleteHeader('Authorization');
        } else {
          // it's an SmartMixin API call
          client.setHeader('Authorization', `Bearer ${token}`);
        }

        if (debugEmailRef.current && isAdminRef.current && !options?.skipLoginAs) {
          client.setHeader('X-Login-As', `${debugEmailRef.current}`);
        } else {
          client.deleteHeader('X-Login-As');
        }

        if (options && options.headers) {
          Object.entries(options.headers).forEach(([key, value]) => {
            client.setHeader(key, value);
          });
        }

        let res;
        if (method === 'GET') {
          res = await client.get(url, params);
        } else if (method === 'POST') {
          res = await client.post(url, params);
        } else if (method === 'PATCH') {
          res = await client.patch(url, params);
        } else if (method === 'PUT') {
          res = await client.put(url, params);
        } else if (method === 'DELETE') {
          res = await client.delete(url, params);
        } else {
          throw new Error(`HTTP method not allowed: ${method}`);
        }

        setLoading(false);

        if (res.headers && Object.keys(res.headers).includes('x-api-version')) {
          if (
            SUPPORTED_API_VERSION < parseInt(res.headers['x-api-version'], 10)
          ) {
            dispatch(setUpgradeRequired(true));
          }
        }

        if (!res.ok) {
          const err = errorHandler(res as ApiErrorResponse<unknown>);

          if (
            !(options && options.silent) &&
            err.details &&
            !ErrorCode.HANDLED_ERROR_CODES.includes(err.details.code.toString())
          ) {
            if (err.statusCode === StatusCodes.NOT_FOUND) {
              if (options && options.ignore404) {
              } else {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else if (err.statusCode === StatusCodes.UNAUTHORIZED) {
              if (options && options.ignore401) {
              } else {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else if (err.statusCode === StatusCodes.CONFLICT) {
              if (options && options.ignore409) {
              } else {
                dispatch(
                  setAlert({message: err.details.message, type: 'ERROR'}),
                );
              }
            } else {
              dispatch(setAlert({message: err.details.message, type: 'ERROR'}));
            }
          }

          return Promise.reject(new ApiError(err));
        } else {
          return res.data as T;
        }
      };

      const methodCall =
        options && options.debounce
          ? debounce(fetchData, options.debounce)
          : fetchData;

      setLoading(true);

      return methodCall(apiCall, storedTokenRef.current) as Promise<T>;
    },
    [dispatch],
  );
  return {fetch, loading};
};

export default useApiClient;
